import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Facebook, Linkedin, Instagram, Youtube } from "lucide-react";
import { company } from "../mock/mockData";
import { useSettings } from "../context/SettingsContext";
import { useContent } from "../context/ContentContext";
import { subscribeNewsletter } from "../services/api";
import logo from "../assets/logo.jpeg";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";

const Footer = () => {
  const [email, setEmail] = useState("");
  const { toast } = useToast();
  const { settings } = useSettings();
  const { content, categories } = useContent();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribeNewsletter(email);
      toast({ title: "Subscribed!", description: "Thanks for joining our newsletter." });
      setEmail("");
    } catch (err) {
      toast({ title: "Subscription failed", description: "Please try again.", variant: "destructive" });
    }
  };

  return (
    <footer className="bg-[#14171c] text-neutral-400">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-white">
              Tell us about your project
            </h3>
            <p className="text-neutral-400 mt-1">
              Receive a custom quotation within 24 hours — factory direct.
            </p>
          </div>
          <Link to="/contact">
            <Button className="bg-[#C9A227] hover:bg-[#A6841B] text-white font-semibold rounded-full px-7 h-12 text-base">
              Request a Quote
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={logo} alt="Best For Medals" className="h-12 w-12 object-contain bg-white rounded-md p-0.5" />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white text-[15px]">BEST FOR MEDALS</div>
              <div className="text-[10px] tracking-[0.2em] text-[#C9A227] font-semibold">HK LIMITED</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed">
            {content.footerAbout}
          </p>
          <div className="flex gap-3 mt-5">
            {[Facebook, Linkedin, Instagram, Youtube].map((Icon, i) => (
              <a key={i} href="#" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#C9A227] flex items-center justify-center transition-colors">
                <Icon className="w-4 h-4 text-white" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4">Products</h4>
          <ul className="space-y-2.5 text-sm">
            {categories.map((c) => (
              <li key={c.slug}>
                <Link to={`/category/${c.slug}`} className="hover:text-[#C9A227] transition-colors">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4">Company</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/factory" className="hover:text-[#C9A227] transition-colors">Factory & Manufacturing</Link></li>
            <li><Link to="/gallery" className="hover:text-[#C9A227] transition-colors">Gallery</Link></li>
            <li><Link to="/blog" className="hover:text-[#C9A227] transition-colors">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-[#C9A227] transition-colors">Contact Us</Link></li>
            <li><Link to="/contact" className="hover:text-[#C9A227] transition-colors">Request a Quote</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4">Get In Touch</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5"><Mail className="w-4 h-4 text-[#C9A227] mt-0.5" /> {settings.email}</li>
            <li className="flex items-start gap-2.5"><Phone className="w-4 h-4 text-[#C9A227] mt-0.5" /> {settings.phone}</li>
            <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-[#C9A227] mt-0.5" /> {settings.addressCN}</li>
          </ul>
          <form onSubmit={subscribe} className="mt-5 flex gap-2">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
              className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500 h-10" />
            <Button type="submit" className="bg-[#C9A227] hover:bg-[#A6841B] h-10 px-3"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <span>© {new Date().getFullYear()} {company.legal}. All Rights Reserved.</span>
          <span>Custom Medal Manufacturer • Sportswear • Event Merchandise</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
